import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase-config';

const FacultyDetail = () => {
  const { id } = useParams();
  const [faculty, setFaculty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFaculty = async () => {
      setLoading(true);
      try {
        const facultyRef = doc(db, 'faculty', id);
        const facultySnap = await getDoc(facultyRef);
        if (facultySnap.exists()) {
          setFaculty({ id: facultySnap.id, ...facultySnap.data() });
        }
      } catch (error) {
        console.error('Error loading faculty:', error);
      }
      setLoading(false);
    };
    loadFaculty();
  }, [id]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <div style={{ width: '50px', height: '50px', borderRadius: '50%', border: '4px solid rgba(14,165,233,0.1)', borderTopColor: 'var(--clr-blue)', animation: 'spin 0.8s linear infinite' }}></div>
      </div>
    );
  }

  if (!faculty) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '80px 0' }}>
        <i className="fas fa-user-slash" style={{ fontSize: '3rem', color: 'var(--clr-rose)' }}></i>
        <h2 style={{ marginTop: '20px' }}>Faculty not found</h2>
        <Link to="/faculty" style={{ display: 'inline-block', marginTop: '20px', padding: '12px 28px', background: 'linear-gradient(135deg, var(--clr-blue), #0284c7)', borderRadius: '40px', color: 'white' }}>Back to Faculty</Link>
      </div>
    );
  }

  const colors = {
    'Governing Body': '#8b5cf6',
    'Teaching': '#0ea5e9',
    'Administration': '#10b981'
  };
  const color = colors[faculty.category] || '#0ea5e9';
  const icons = {
    'Governing Body': 'fa-landmark',
    'Teaching': 'fa-chalkboard-user',
    'Administration': 'fa-building-user'
  };
  const icon = icons[faculty.category] || 'fa-user-tie';

  return (
    <div className="container" style={{ paddingTop: '40px' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link to="/faculty" style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', marginBottom: '30px', padding: '12px 24px', background: 'rgba(255,255,255,0.4)', borderRadius: '50px', transition: 'all 0.3s ease' }}>
          <i className="fas fa-arrow-left"></i> Back to Faculty
        </Link>

        <div style={{
          background: 'rgba(255, 255, 255, 0.5)',
          backdropFilter: 'blur(20px) saturate(140%)',
          borderRadius: '24px',
          overflow: 'hidden',
          border: '1px solid var(--clr-blue-border)'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '320px 1fr',
            gap: '40px',
            padding: '40px',
            background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.02), rgba(30, 41, 59, 0.02))'
          }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: '100%',
                height: '340px',
                borderRadius: '16px',
                overflow: 'hidden',
                background: 'linear-gradient(135deg, #0f172a, #1e293b)',
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}>
                <img
                  src={faculty.image || 'https://randomuser.me/api/portraits/men/32.jpg'}
                  alt={faculty.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
                />
              </div>
              {faculty.category && (
                <div style={{
                  position: 'absolute',
                  bottom: '-15px',
                  right: '20px',
                  padding: '8px 20px',
                  borderRadius: '30px',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  background: color,
                  color: 'white',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>
                  <i className={`fas ${icon}`}></i> {faculty.category}
                </div>
              )}
            </div>

            <div>
              <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '2.5rem', marginBottom: '8px' }}>{faculty.name}</h1>
              <p style={{ fontSize: '1.2rem', fontWeight: 600, color: 'var(--clr-blue)', marginBottom: '15px' }}>{faculty.designation || 'Professor'}</p>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', margin: '20px 0' }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: 'rgba(255, 255, 255, 0.4)',
                  backdropFilter: 'blur(20px) saturate(140%)',
                  padding: '8px 18px',
                  borderRadius: '40px',
                  fontSize: '0.85rem',
                  fontWeight: 500,
                  border: '1px solid rgba(255, 255, 255, 0.6)'
                }}>
                  <i className="fas fa-briefcase" style={{ color: 'var(--clr-blue)' }}></i>
                  <span>{faculty.experience || 'Experience not specified'}</span>
                </div>
              </div>

              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                background: 'rgba(14, 165, 233, 0.15)',
                padding: '10px 20px',
                borderRadius: '40px',
                fontWeight: 600,
                marginTop: '10px'
              }}>
                <i className="fas fa-building"></i>
                Department of {faculty.department || 'Medical Sciences'}
              </div>
            </div>
          </div>

          <div style={{ padding: '0 40px 40px 40px' }}>
            {/* Qualification */}
            <div style={{ marginBottom: '35px' }}>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.4rem',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                paddingBottom: '10px',
                borderBottom: '2px solid rgba(14, 165, 233, 0.3)'
              }}>
                <i className="fas fa-graduation-cap" style={{ color: 'var(--clr-emerald)' }}></i>
                Academic Qualification
              </h3>
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1), rgba(5, 150, 105, 0.05))',
                borderLeft: '5px solid var(--clr-emerald)',
                padding: '20px 25px',
                borderRadius: '16px'
              }}>
                <p style={{ fontSize: '1rem', lineHeight: '1.6', color: '#065f46', fontWeight: 500 }}>
                  {faculty.qualification || 'Qualification details coming soon.'}
                </p>
              </div>
            </div>

            {/* Achievements */}
            {faculty.achievements && (
              <div style={{ marginBottom: '35px' }}>
                <h3 style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: '1.4rem',
                  marginBottom: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  paddingBottom: '10px',
                  borderBottom: '2px solid rgba(14, 165, 233, 0.3)'
                }}>
                  <i className="fas fa-trophy" style={{ color: 'var(--clr-amber)' }}></i>
                  Achievements & Awards
                </h3>
                <div style={{
                  background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.1), rgba(217, 119, 6, 0.05))',
                  borderLeft: '5px solid var(--clr-amber)',
                  padding: '20px 25px',
                  borderRadius: '16px'
                }}>
                  <p style={{ fontSize: '0.95rem', lineHeight: '1.7', color: '#92400e' }}>{faculty.achievements}</p>
                </div>
              </div>
            )}

            {/* Experience */}
            <div style={{ marginBottom: '35px' }}>
              <h3 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: '1.4rem',
                marginBottom: '15px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                paddingBottom: '10px',
                borderBottom: '2px solid rgba(14, 165, 233, 0.3)'
              }}>
                <i className="fas fa-chalkboard-user" style={{ color: 'var(--clr-blue)' }}></i>
                Professional Experience
              </h3>
              <div style={{
                background: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1), rgba(2, 132, 199, 0.05))',
                borderLeft: '5px solid var(--clr-blue)',
                padding: '20px 25px',
                borderRadius: '16px'
              }}>
                <p style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--clr-blue)' }}>
                  <i className="fas fa-calendar-alt"></i> {faculty.experience || 'Experience details not specified'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FacultyDetail;